import { FC, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import axios from 'axios';
import { useAuth } from '../../../modules/auth';
import { useAlarmedBoxes } from '../../sensors-v2/hooks/useAlarmedBoxes';
import { PageCard, SectionLabel, StatBlock, TOKENS } from '../../../components/design-system';
import { maxSeverity } from '../../../components/box-status-card/severity';
import { isBoxOnline } from '../../../components/box-status-card/presenceStatus';

const API_URL = process.env.REACT_APP_API_URL;

interface BoxRow {
  box_id: number;
  firmware_protocol?: string | null;
  last_seen_at?: string | null;
}

// Fleet roster used for both the total count and offline detection.
// Pulls from the same /measuring-box/:company_id/maps endpoint the
// LeafletMap uses, so the dashboard hero and the map agree on what's
// offline. The maps endpoint returns last_seen_at + firmware_protocol,
// which is exactly what isBoxOnline needs.
function useFleetRoster(company_id: number | undefined) {
  return useQuery(
    ['dashboard', 'fleet-roster', company_id],
    async () => {
      if (!company_id) return [] as BoxRow[];
      const { data } = await axios.get<{ data?: BoxRow[] }>(
        `${API_URL}/measuring-box/${company_id}/maps`,
        { params: { company_id, take: 0, skip: 10_000 } },
      );
      return data?.data ?? [];
    },
    { enabled: !!company_id, refetchInterval: 60_000, refetchOnWindowFocus: false },
  );
}

export const FleetHealthHero: FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const companyId = currentUser?.last_company_id ? +currentUser.last_company_id : undefined;

  const { data: alarmedData } = useAlarmedBoxes({
    sort: 'gravity_desc', page: 1, items_per_page: 200,
    company_id: companyId,
  });
  const { data: roster = [] } = useFleetRoster(companyId);

  const { high, medium, ok, offline } = useMemo(() => {
    // Index alarmed boxes by id so a single pass over the roster
    // resolves "what's the status of each box". An alarmed box wins
    // its severity over presence — same precedence as the map.
    const sevById = new Map<number, 'high' | 'medium' | null>();
    for (const b of (alarmedData?.data ?? [])) {
      if (!b.active_alarms.length) continue;
      const sev = maxSeverity(b.active_alarms);
      if (sev === 'high' || sev === 'medium') {
        sevById.set(Number((b as any).box_id ?? (b as any).id), sev);
      }
    }
    let h = 0;
    let m = 0;
    let o = 0;
    let off = 0;
    const now = Date.now();
    for (const box of roster) {
      const sev = sevById.get(Number(box.box_id));
      if (sev === 'high') { h += 1; continue; }
      if (sev === 'medium') { m += 1; continue; }
      if (isBoxOnline(box, now)) o += 1;
      else off += 1;
    }
    return { high: h, medium: m, ok: o, offline: off };
  }, [alarmedData?.data, roster]);

  return (
    <PageCard variant="hero">
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: TOKENS.spacing.md,
      }}>
        <SectionLabel>Status das caixas</SectionLabel>
        <span
          role="button"
          onClick={() => navigate('/sensors-v2')}
          style={{
            color: TOKENS.color.action,
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Ver sensores →
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: TOKENS.spacing.md }}>
        <StatBlock value={high} label="Alta" tone="high" onClick={() => navigate('/sensors-v2')} />
        <StatBlock value={medium} label="Média" tone="medium" onClick={() => navigate('/sensors-v2')} />
        <StatBlock value={ok} label="OK" tone="low" />
        <StatBlock value={offline} label="Offline" tone="neutral" onClick={() => navigate('/map')} />
      </div>
    </PageCard>
  );
};
